<template>
	<div class="mini-menu-container">
		<div class="mini-menu">
			<div class="mini-menu-header">
				<div class="menu-selector"><AppSelect ref="menu-selector" :values="menus" :selected-index="menuIndex" @selected="index => menuIndex = index" /></div>
				<div class="save-container"><AppButton :text="getString('save')" @click="saveAvailabilities" /></div>
			</div>
			<div class="mini-menu-body">
				<div class="mini-menu-categories" v-if="selectedMenu && selectedMenuCategories.length > 0">
					<div v-for="category in selectedMenuCategories" class="mini-menu-category" :key="category.menu_category_id" :style="'--category-color: '+category.color+';--category-text-color: '+categoryTextColor(category.color)">
						<div class="category-header-container">
							<div class="category-header">
								<div class="category-availability" @click="categoryClick(category)" ><AppCheckbox :is-active="isCategorySelected(category)" :auto-toggle="false" /></div>
								<div class="category-name-container"><div class="category-name" @click="toggleDropdown(category)"><h3>{{ category.name }}</h3></div></div>
								<div class="category-chevron" @click="toggleDropdown(category)"><AppChevronIcon :direction="category.dropdownActive ? 'up' : 'down'" /></div>
							</div>
						</div>
						<div class="category-body">
							<AppDropdown square-corner :collapsed="!category.dropdownActive" white>
								<div class="items-container" v-if="category.items && categoryItems(category).length > 0">
									<div class="item" v-for="item in categoryItems(category)" :key="item.menu_item_id" @click="itemClick(item)">
										<div class="item-availability">
											<AppCheckbox v-if="item.available !== null" :auto-toggle="false" :is-active="!!item.available" />
										</div>
										<div class="item-name"><p>{{ item.name }}</p></div>
										<div v-if="item.available === null" class="icon-availability-container" v-html="getIcon('iconHourglass')"></div>
									</div>
								</div>
								<div class="no-items-container" v-else>
									<p>{{ getString("no_items_in_category") }}</p>
								</div>
							</AppDropdown>
						</div>
					</div>
				</div>
				<div class="no-categories" v-if="selectedMenu && selectedMenuCategories.length === 0">
					<p>{{ getString("no_categories", [userStore.menuWord]) }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AppSelect from "@/shared/components/AppSelect.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import {useLanguageStore, useSvgStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import {isTextColorDark} from "@/shared/helpers/ui";
import API from '@/shared/helpers/API.js';

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, getIcon: useSvgStore().get, userStore, menuStore, notificationStore};
	},
	name: "MiniMenu",
	components: {AppChevronIcon, AppCheckbox, AppDropdown, AppButton, AppSelect},
	emits: ["saved"],
	data() {
		return {
			menuIndex: 0,
		}
	},
	computed: {
		menus() {
			return this.menuStore.sortedMenus.map(menu => menu.name);
		},
		selectedMenu() {
			return this.menus.length > 0 ? this.menuStore.sortedMenus[this.menuIndex] : null;
		},
		selectedMenuCategories() {
			return this.selectedMenu && this.selectedMenu.sortedCategories ? this.selectedMenu.sortedCategories : [];
		}
	},
	methods: {
		categoryItems(category) {
			return category.sortedItems;
		},
		itemClick(item) {
			if(item.available !== null) {
				item.available = !item.available;
				item.availableHasChanged = true;
			}
		},
		isCategorySelected(category) {
			return Object.values(category.items).every(item => item.available === true || item.available === null);
		},
		categoryClick(category) {
			let isCategorySelected = this.isCategorySelected(category);
			Object.values(category.items).forEach(item => {
				if(item.available !== null) {
					item.available = !isCategorySelected;
					item.availableHasChanged = true;
				}
			});
		},
		categoryTextColor(color) {
			if(isTextColorDark(color)) {
				return "#000000";
			} else {
				return "#ffffff";
			}
		},
		toggleDropdown(category) {
			this.selectedMenuCategories.forEach(c => {
				if(c.menu_category_id !== category.menu_category_id) {
					c.dropdownActive = false;
				} else {
					c.dropdownActive = !c.dropdownActive;
				}
			});
		},
		saveAvailabilities() {

			let items = [];
			this.selectedMenuCategories.forEach(category => {
				Object.values(category.items).forEach(item => {
					if(item.availableHasChanged && item.available !== null) {
						items.push({
							menu_item_id: item.menu_item_id,
							available: item.available
						});
					}
				});
			});

			API.init().updateMenuItemsAvailabilities(this.userStore.currBusinessId, JSON.stringify(items), (r) => {
				if(r && r.result === "OK") {
					Object.values(this.menuStore.items).forEach(item => {
						let itemToUpdate = items.find(i => i.menu_item_id === item.menu_item_id);
						if(itemToUpdate) {
							item.available = itemToUpdate.available;
							item.availableHasChanged = false;
						}
					});
					this.notificationStore.showNotification("generic-success");
					this.$emit("saved");
					this.selectedMenuCategories.forEach(c => c.dropdownActive = false);
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});

		}
	},
}
</script>

<style lang="scss" scoped>
@keyframes availableColorTimetables {
	0% {
		fill: #FDDE00;
	}
	100% {
		fill: #a28e09;
	}
}
.mini-menu-container {
	width: 500px;
	background-color: var(--color-bg);
	border-radius: 13px;
	margin: var(--main-padding);
	height: calc(100% - 2 * var(--main-padding));
	--mini-menu-padding: 20px;
	overflow: hidden;
	margin-left: 44px;

	@media (max-width: 900px) {
		--mini-menu-padding: 12px;
		width: 400px;
	}

	@media (max-width: 500px) {
		width: 100%;
	}

	.mini-menu {
		display: flex;
		flex-direction: column;
		height: 100%;

		.mini-menu-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 100%;
			padding: var(--mini-menu-padding);
			box-shadow: 0 0 11px var(--color-box-shadow-2);
			flex-wrap: wrap;
			gap: 8px;

			.menu-selector {
				margin-right: 10px;
			}

			.save-container {
				margin-left: auto;
			}
		}

		.mini-menu-body {
			padding: var(--mini-menu-padding);
			overflow-y: auto;

			.mini-menu-categories {
				width: 100%;
				box-shadow: 0 1px 2px var(--color-box-shadow-3);
				border-radius: 13px;
				overflow: hidden;

				.mini-menu-category {
					width: 100%;

					.category-header-container {

						.category-header {
							width: 100%;
							display: flex;
							align-items: stretch;
							border-bottom: 1px solid var(--color-alpha-2);

							.category-availability {
								width: 45px;
								display: flex;
								justify-content: center;
								align-items: center;
								user-select: none;
							}

							.category-name-container {
								@include noSelect;
								width: calc(100% - 75px);
								min-height: 36px;

								.category-name {
									width: 82%;
									min-height: 36px;
									padding: 8px 33px 8px 10px;
									background-color: var(--category-color);
									position: relative;
									cursor: pointer;
									display: flex;
									align-items: center;

									&:after {
										content: "";
										display: block;
										width: 40px;
										height: 100%;
										position: absolute;
										top: 0;
										right: -10px;
										background-color: var(--category-color);
										transform: skew(-15deg);
										border-radius: 5px;
									}

									h3 {
										font-size: 16px;
										color: var(--category-text-color);
										font-weight: 400;
										overflow: hidden;
										text-overflow: ellipsis;
										white-space: nowrap;
									}
								}

							}

							.category-chevron {
								width: 30px;
								display: flex;
								justify-content: center;
								align-items: center;
								background-color: var(--color-bg-primary-2);
								cursor: pointer;
								user-select: none;
							}
						}
					}

					.category-body {

						:deep(.dropdown-content) {
							background: transparent;
						}

						.no-items-container {
							margin-bottom: 10px;

							p {
								font-size: 14px;
							}
						}

						.items-container {
							margin-bottom: 10px;
							box-shadow: 0 1px 2px var(--color-box-shadow-3);
							border-radius: 13px;
							overflow: hidden;

							.item {
								display: flex;
								align-items: center;
								border-bottom: 1px solid var(--color-border-alpha);
								cursor: pointer;
								background-color: var(--color-bg-primary-1);
								@include noSelect;

								.item-availability {
									width: 45px;
									display: flex;
									justify-content: center;
									align-items: center;
									user-select: none;
								}

								.icon-availability-container {
									width: 12px;
									height: 12px;
									display: flex;
									justify-content: center;
									align-items: center;

									:deep(svg) {
										.fill {
											animation: availableColorTimetables 1.5s linear 0s infinite alternate;
										}
									}
								}

								.item-name {
									width: calc(100% - 70px);
									min-height: 36px;
									display: flex;
									align-items: center;
									padding-right: 8px;

									p {
										font-size: 14px;
										color: var(--color-txt);
										font-weight: 400;
										overflow: hidden;
										text-overflow: ellipsis;
										white-space: nowrap;
									}
								}
							}

							.item:last-child {
								border-bottom: none;
							}
						}
					}

					&:first-child {
						.category-header-container {
							.category-header {
								border-radius: 13px 13px 0 0;
								border-top: 1px solid var(--color-border-2);

								.category-chevron {
									border-top-right-radius: 13px;
								}
							}
						}
					}

					&:last-child {
						.category-header-container {
							.category-header {
								.category-chevron {
									border-bottom-right-radius: 13px;
								}
							}
						}
					}

				}

			}

		}
	}
}
</style>